import { getConsentMode } from '../../services/consentMode';
import {
    captureCampaignParamsFromLocation,
    DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST,
} from './campaignParamsCapture';

export type CtaParametersForwardingMode = 'none' | 'allowlist' | 'all';

export { DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST };

function normalizeAllowlist(allowlist: readonly string[]): string[] {
    const normalized = new Set<string>();
    for (const key of allowlist) {
        const trimmed = key.trim();
        if (trimmed) {
            normalized.add(trimmed);
        }
    }
    return Array.from(normalized);
}

function getCurrentSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location?.search ?? '');
}

function extractCurrentAllowlistedParams(allowlist: readonly string[]): Record<string, string> {
    const current = getCurrentSearchParams();
    const out: Record<string, string> = {};
    for (const key of allowlist) {
        const value = current.get(key);
        if (value) {
            out[key] = value;
        }
    }
    return out;
}

function appendRecord(params: URLSearchParams, values: Record<string, string>): void {
    for (const [key, value] of Object.entries(values)) {
        if (value && !params.has(key)) {
            params.set(key, value);
        }
    }
}

export function getForwardedUrlParams(
    mode: CtaParametersForwardingMode,
    allowlist: readonly string[] = DEFAULT_CTA_PARAMETERS_FORWARDING_ALLOWLIST
): URLSearchParams {
    const forwarded = new URLSearchParams();
    if (getConsentMode() !== 'full') return forwarded;
    if (mode === 'none') return forwarded;

    const allowedKeys = normalizeAllowlist(allowlist);
    const storedCampaignParams =
        allowedKeys.length > 0 ? captureCampaignParamsFromLocation(allowedKeys) : {};

    if (mode === 'all') {
        getCurrentSearchParams().forEach((value, key) => {
            if (value && !forwarded.has(key)) {
                forwarded.set(key, value);
            }
        });
        appendRecord(forwarded, storedCampaignParams);
        return forwarded;
    }

    appendRecord(forwarded, storedCampaignParams);
    appendRecord(forwarded, extractCurrentAllowlistedParams(allowedKeys));
    return forwarded;
}

export function appendForwardedParams(
    target: URLSearchParams,
    forwarded: URLSearchParams
): boolean {
    let changed = false;
    forwarded.forEach((value, key) => {
        if (!target.has(key)) {
            target.set(key, value);
            changed = true;
        }
    });
    return changed;
}
